import type {ChangeEvent, ReactElement} from 'react';
import {useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {MOBILE_CSS_PROP_PREFIX, TABLET_CSS_PROP_PREFIX} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerCanvasDevice} from '@EmailMakerReact/email-maker-react.types';
import {setComponentStyle, useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {setDeviceMediaQuery} from '@EmailMakerReact/lib/email-maker-media-query-library';
import type {ResponsiveVariantSliderProps} from '../../slider-control.types';
import {SLIDER_HIDE_INPUT_PROPS} from '../../slider-control.types';
import {buildRangeFillStyle, parseAndClamp, parseRawCssToNumeric} from '../../slider-control.utils';
import {SliderBody} from '../slider-body/slider-body';
import styles from '../../slider-control.module.scss';

export function ResponsiveVariantSlider({descriptor, effectiveMax, component, activeProperty, responsiveTag, isHidden, editor}: ResponsiveVariantSliderProps): ReactElement {
  const rawValue = useStyleProperty(editor, component, activeProperty);
  const numericValue = parseRawCssToNumeric(rawValue ?? '', {...descriptor, property: activeProperty});
  const step = descriptor.step ?? 1;
  const min = descriptor.min;
  const max = effectiveMax;
  const unit = descriptor.unit;
  const hideRangeInput = SLIDER_HIDE_INPUT_PROPS.has(descriptor.property);

  const deviceID = activeProperty.startsWith(TABLET_CSS_PROP_PREFIX) ? EmailMakerCanvasDevice.TABLET : EmailMakerCanvasDevice.MOBILE;
  const cssPrefix = deviceID === EmailMakerCanvasDevice.TABLET ? TABLET_CSS_PROP_PREFIX : MOBILE_CSS_PROP_PREFIX;
  const [isInputActive, setIsInputActive] = useState(false);

  function writeResponsive(val: number): void {
    if (!editor) {
      return;
    }
    const cssValue = `${val}${unit}`;
    setDeviceMediaQuery(editor, {
      property: activeProperty,
      value: cssValue,
      id: component.ccid,
      deviceID,
      cssPrefix,
    });
    setComponentStyle(editor, {property: activeProperty, value: cssValue, component});
  }

  function handleSliderChange(e: ChangeEvent<HTMLInputElement>): void {
    writeResponsive(parseAndClamp(e.target.value, min, max));
  }

  function handleTextChange(val: number): void {
    writeResponsive(parseAndClamp(String(val), min, max));
  }

  function handleStep(direction: 1 | -1): void {
    writeResponsive(parseAndClamp(String(numericValue + direction * step), min, max));
  }

  return (
    <div className={`${styles.sliderContainer} ${isHidden ? styles.sliderContainerHidden : ''}`}>
      <div className={styles.labelRow}>
        <span className={styles.label}>
          <Text val={responsiveTag} size={TextSize.SM} bold className={styles.responsiveTag} />
          <Text val={window.i18next.t(descriptor.labelKey)} size={TextSize.MD} />
        </span>
        <SliderBody
          descriptor={descriptor}
          displayValue={numericValue}
          effectiveMax={effectiveMax}
          isInputActive={isInputActive}
          numericValue={numericValue}
          onActivate={(): void => {
            setIsInputActive(true);
          }}
          onDeactivate={(): void => {
            setIsInputActive(false);
          }}
          onStep={handleStep}
          onTextChange={handleTextChange}
        />
      </div>
      <input
        aria-label={descriptor.property}
        className={`${styles.rangeInput} ${hideRangeInput ? styles.rangeInputHidden : ''}`}
        max={max}
        min={min}
        onChange={handleSliderChange}
        step={step}
        style={buildRangeFillStyle(isNaN(numericValue) ? min : numericValue, min, max)}
        type="range"
        value={isNaN(numericValue) ? min : numericValue}
      />
    </div>
  );
}
